/**
 * Error code handling for placebetsport and executeOrder API responses.
 * Maps backend error codes to appropriate actions.
 */

export const ERROR_CODES = {
  /** Authorization required - show auth modal */
  AUTH: [270, 271],
  /** Recover betslip - apply additionalInfo (odds/event changes) */
  RECOVER_BETSLIP: [241, 254, 255, 256, 260],
  /** Recover betslip errors that are transient — safe to auto-retry with acceptOddsChanges */
  RETRIABLE_RECOVER: [241, 255, 256, 260],
  /** Event-specific error - show message with ev_id, then recover */
  EVENT_SPECIFIC_RECOVER: [253],
  /** Bonus rules changed - reload page */
  BONUS_RULES_CHANGED: 604,
  /** Self-excluded - update user state, no error toast */
  SELF_EXCLUDED: 1916,
} as const;

export type ErrorActionType =
  | "auth"
  | "recover_betslip"
  | "event_specific_recover"
  | "bonus_rules_changed"
  | "self_excluded"
  | "generic";

export interface ErrorAction {
  action: ErrorActionType;
  message: string;
  errorCode: number;
  additionalInfo?: any[];
  eventId?: number;
}

export interface ApiErrorResult {
  errorCode?: number;
  errorCodeDescription?: string | null;
  errorDescription?: string | null;
  additionalInfo?: any[];
}

const RECOVER_CODES = new Set<number>(ERROR_CODES.RECOVER_BETSLIP);
const EVENT_SPECIFIC_CODES = new Set<number>(
  ERROR_CODES.EVENT_SPECIFIC_RECOVER,
);
const AUTH_CODES = new Set<number>(ERROR_CODES.AUTH);

/**
 * Maps API error response to an ErrorAction with appropriate handling.
 */
export function mapErrorToAction(
  result: ApiErrorResult | null | undefined,
  fallbackMessage: string = "Please try again later",
): ErrorAction {
  const errorCode = result?.errorCode ?? 0;
  const message =
    result?.errorCodeDescription || result?.errorDescription || fallbackMessage;
  const additionalInfo = result?.additionalInfo;

  if (AUTH_CODES.has(errorCode)) {
    return {
      action: "auth",
      message,
      errorCode,
      additionalInfo,
    };
  }

  if (RECOVER_CODES.has(errorCode)) {
    return {
      action: "recover_betslip",
      message,
      errorCode,
      additionalInfo,
    };
  }

  if (EVENT_SPECIFIC_CODES.has(errorCode)) {
    const eventId = additionalInfo?.[0]?.ev_id ?? additionalInfo?.[0]?.ev_Id;
    return {
      action: "event_specific_recover",
      message: eventId != null ? message.replace("{id}", String(eventId)) : message,
      errorCode,
      additionalInfo,
      eventId,
    };
  }

  if (errorCode === ERROR_CODES.BONUS_RULES_CHANGED) {
    return {
      action: "bonus_rules_changed",
      message: "BonusRulesChanged",
      errorCode,
      additionalInfo,
    };
  }

  if (errorCode === ERROR_CODES.SELF_EXCLUDED) {
    return {
      action: "self_excluded",
      message,
      errorCode,
      additionalInfo,
    };
  }

  return {
    action: "generic",
    message,
    errorCode,
    additionalInfo,
  };
}

/**
 * Check if error code requires auth flow (270, 271).
 */
export function isAuthError(errorCode?: number): boolean {
  return errorCode != null && AUTH_CODES.has(errorCode);
}

/**
 * Check if error code requires betslip recovery (241, 254, 255, 256, 260).
 */
export function isRecoverError(errorCode?: number): boolean {
  return errorCode != null && RECOVER_CODES.has(errorCode);
}
